/**
 * Spec 0052 (U2) — per-tenant generic integration-settings (pass-through)
 * management (saas-auth SASATH-0067).
 *
 * SUPER_ADMIN-only grouping on the Settings tab, beside the native GHL section
 * (spec 0045) and the resource-limit plan section (spec 0050/0051). The caller
 * (TenantSettings) gates rendering on `operator?.isSuperAdmin`; this component
 * additionally degrades to a contained "SUPER_ADMIN required" state if any call
 * returns 403. It self-fetches the tenant's groups on mount with isolated
 * loading/empty/error/403 states — it is NOT folded into tenantWorkspaceLoader,
 * so an ordinary SAAS_ADMIN's Settings tab is never broken by a 403 here (same
 * rationale as the GHL section / 0044).
 *
 * SEPARATE FROM THE NATIVE GHL SCREEN (spec 0045): this is the generic
 * pass-through store. A backend MAY create a group literally named `GHL` here —
 * that is a DIFFERENT thing from the native GoHighLevel integration, which is
 * unchanged. The section is labelled "Integration Settings (pass-through)" to
 * keep the two visually distinct.
 *
 * SECRET HYGIENE (load-bearing — mirrors the GHL token discipline exactly). A
 * key marked `isSecret` is masked on read (`valueSet` + a short `valueSuffix`,
 * never the full value); it is rendered as "•••• {suffix}" / "Set" / "Not set",
 * NEVER a pre-filled editable field. A "Replace" action reveals a write-only
 * password input; on save the secret's `value` is OMITTED unless the admin typed
 * one (omit → the backend preserves the stored secret via SASATH-0066
 * merge-on-absent; non-blank → replaces). "Clear" deletes the key (delete-key —
 * there is no blank-to-clear). Every group mutation re-sends the group's other
 * settings with secret values OMITTED, so an unrelated edit/toggle never clears a
 * stored secret. The typed secret lives ONLY in transient component state — never
 * persisted, never logged, never written to local/sessionStorage.
 *
 * CONTRACT STATUS — aligned to the SHIPPED SASATH-0067 wire (spec-0053 hotfix):
 * a group's key array reads as `keys` and the upsert request array is `entries`
 * (the secret discriminant stays `isSecret`). See the `IntegrationGroup` /
 * `UpsertIntegrationGroupRequest` notes in `types.ts` for the confirmed shape.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  IntegrationGroup,
  IntegrationSetting,
  UpsertIntegrationGroupRequest,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

// §7-confirmed parity: a group/key name is `[A-Za-z0-9._-]`, max 100 chars,
// case-sensitive (validated client-side for fast feedback; the backend 400 is
// surfaced too). Values are opaque strings and are NOT charset-restricted.
const NAME_PATTERN = /^[A-Za-z0-9._-]+$/;
const NAME_MAX_LENGTH = 100;

type NameError = 'required' | 'tooLong' | 'invalidChars' | null;

function validateName(name: string): NameError {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'required';
  if (trimmed.length > NAME_MAX_LENGTH) return 'tooLong';
  if (!NAME_PATTERN.test(trimmed)) return 'invalidChars';
  return null;
}

/**
 * Build the upsert `entries` array that PRESERVES a group's existing keys:
 * non-secret keys re-send their plain `value`; secret keys are sent WITHOUT a
 * `value` so the backend keeps the stored secret (merge-on-absent). Optionally
 * excludes one key (the one being added/edited/removed, which the caller appends
 * with its own entry).
 */
function preserveEntries(
  keys: IntegrationSetting[],
  excludeKey?: string,
): UpsertIntegrationGroupRequest['entries'] {
  return keys
    .filter((s) => s.key !== excludeKey)
    .map((s) =>
      s.isSecret
        ? { key: s.key, isSecret: true }
        : { key: s.key, isSecret: false, value: s.value },
    );
}

function nameErrorText(
  t: (key: string, fallback: string) => string,
  err: NameError,
): string | null {
  if (err === 'required') return t('integrationSettings.errors.nameRequired', 'A name is required.');
  if (err === 'tooLong')
    return t('integrationSettings.errors.nameTooLong', 'Use at most 100 characters.');
  if (err === 'invalidChars')
    return t(
      'integrationSettings.errors.nameInvalidChars',
      'Use only letters, numbers, and the characters . _ -',
    );
  return null;
}

// ---------------------------------------------------------------------------
// One key within a group (a row in the group card).
// ---------------------------------------------------------------------------

interface KeyRowProps {
  setting: IntegrationSetting;
  busy: boolean;
  /** Save an edited NON-SECRET value, or a REPLACED secret (typed value). */
  onSaveValue: (key: string, isSecret: boolean, value: string) => void;
  /** Delete (non-secret) or Clear (secret) — both call delete-key. */
  onRemove: (key: string) => void;
}

function IntegrationKeyRow({ setting, busy, onSaveValue, onRemove }: KeyRowProps) {
  const { t } = useCopy();
  const [editing, setEditing] = useState(false);
  // Transient draft only. For a non-secret edit it is seeded from the current
  // value; for a secret "Replace" it starts BLANK (write-only) and is never
  // seeded from the masked read.
  const [draft, setDraft] = useState('');

  function startEdit() {
    setDraft(setting.isSecret ? '' : setting.value);
    setEditing(true);
  }

  function cancelEdit() {
    // Drop the transient draft so a typed secret never lingers in state/DOM.
    setDraft('');
    setEditing(false);
  }

  function handleSave() {
    onSaveValue(setting.key, setting.isSecret, draft);
    setDraft('');
    setEditing(false);
  }

  const maskedLabel = setting.isSecret
    ? setting.valueSet
      ? setting.valueSuffix
        ? t('integrationSettings.secret.masked', '•••• {{suffix}}', {
            suffix: setting.valueSuffix,
          })
        : t('integrationSettings.secret.set', 'Set')
      : t('integrationSettings.secret.notSet', 'Not set')
    : null;

  return (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, wordBreak: 'break-all', verticalAlign: 'top' }}>
        {setting.key}
      </TableCell>
      <TableCell sx={{ wordBreak: 'break-all', verticalAlign: 'top' }}>
        {editing ? (
          <TextField
            fullWidth
            size="small"
            type={setting.isSecret ? 'password' : 'text'}
            label={
              setting.isSecret
                ? t('integrationSettings.fields.newSecret', 'New secret value')
                : t('integrationSettings.fields.value', 'Value')
            }
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoComplete={setting.isSecret ? 'new-password' : 'off'}
            helperText={
              setting.isSecret
                ? t(
                    'integrationSettings.fields.replaceHelper',
                    'Write-only. Leave blank to keep the current secret.',
                  )
                : undefined
            }
          />
        ) : setting.isSecret ? (
          <Chip size="small" variant="outlined" label={maskedLabel} />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
            {setting.value || '—'}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
        {editing ? (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button size="small" variant="contained" disabled={busy} onClick={handleSave}>
              {t('common.save', 'Save')}
            </Button>
            <Button size="small" disabled={busy} onClick={cancelEdit}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            <Button size="small" disabled={busy} onClick={startEdit}>
              {setting.isSecret
                ? t('integrationSettings.replace', 'Replace')
                : t('common.edit', 'Edit')}
            </Button>
            <Button size="small" color="error" disabled={busy} onClick={() => onRemove(setting.key)}>
              {setting.isSecret
                ? t('integrationSettings.clear', 'Clear')
                : t('common.delete', 'Delete')}
            </Button>
          </Stack>
        )}
      </TableCell>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// One group card: active toggle, its keys, add-key, delete-group.
// ---------------------------------------------------------------------------

interface GroupCardProps {
  tenantId: number;
  group: IntegrationGroup;
  /**
   * The subset of `group.keys` to display (already filtered by the page-level
   * search). Mutations still operate on the FULL `group.keys` so a save during an
   * active search never drops a hidden key or its preserved secret.
   */
  visibleKeys: IntegrationSetting[];
  onChange: (updated: IntegrationGroup) => void;
  onDelete: (groupName: string) => void;
  onForbidden: () => void;
}

function IntegrationGroupCard({
  tenantId,
  group,
  visibleKeys,
  onChange,
  onDelete,
  onForbidden,
}: GroupCardProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);

  // Add-key form.
  const [addOpen, setAddOpen] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newIsSecret, setNewIsSecret] = useState(false);
  const [keyValidation, setKeyValidation] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);

  // Central upsert helper: PUTs the new desired state and threads 403 → contained
  // forbidden, other ApiErrors → the surfaced banner.
  async function runUpsert(req: UpsertIntegrationGroupRequest): Promise<boolean> {
    setBusy(true);
    setSaveError(null);
    try {
      const updated = await apiAdmin.integrationSettings.upsertGroup(tenantId, group.group, req);
      onChange(updated);
      return true;
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return false;
      }
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive(next: boolean) {
    // Re-send the group's settings unchanged (secrets omitted to preserve).
    await runUpsert({ active: next, entries: preserveEntries(group.keys) });
  }

  async function handleAddKey(e: FormEvent) {
    e.preventDefault();
    setKeyValidation(null);
    const trimmedKey = newKey.trim();
    const nameErr = validateName(trimmedKey);
    if (nameErr) {
      setKeyValidation(nameErrorText(t, nameErr));
      return;
    }
    if (group.keys.some((s) => s.key === trimmedKey)) {
      setKeyValidation(
        t('integrationSettings.errors.duplicateKey', 'A key with that name already exists.'),
      );
      return;
    }
    // The new entry: a non-secret carries its value; a secret carries the typed
    // value only when non-blank (otherwise it is created with no stored value).
    const trimmedValue = newValue;
    const entry = newIsSecret
      ? trimmedValue.trim().length > 0
        ? { key: trimmedKey, isSecret: true, value: trimmedValue }
        : { key: trimmedKey, isSecret: true }
      : { key: trimmedKey, isSecret: false, value: trimmedValue };
    const ok = await runUpsert({
      active: group.active,
      entries: [...preserveEntries(group.keys), entry],
    });
    if (ok) {
      snack.success(t('integrationSettings.keyAdded', 'Key added.'));
      setNewKey('');
      setNewValue('');
      setNewIsSecret(false);
      setAddOpen(false);
    }
  }

  async function handleSaveValue(key: string, isSecret: boolean, value: string) {
    // Secret "Replace" with a blank field is a no-op (keep the stored secret) —
    // never a blank upsert, never a clear.
    if (isSecret && value.trim().length === 0) return;
    const entry = isSecret
      ? { key, isSecret: true, value }
      : { key, isSecret: false, value };
    const ok = await runUpsert({
      active: group.active,
      entries: [...preserveEntries(group.keys, key), entry],
    });
    if (ok) snack.success(t('integrationSettings.keyUpdated', 'Key updated.'));
  }

  async function handleRemoveKey(key: string) {
    setBusy(true);
    setSaveError(null);
    try {
      await apiAdmin.integrationSettings.deleteKey(tenantId, group.group, key);
      // The key is gone — reflect it locally (the read never carried the secret
      // value, so nothing sensitive is dropped).
      onChange({ ...group, keys: group.keys.filter((s) => s.key !== key) });
      snack.success(t('integrationSettings.keyRemoved', 'Key removed.'));
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteGroup() {
    setBusy(true);
    try {
      await apiAdmin.integrationSettings.deleteGroup(tenantId, group.group);
      snack.success(t('integrationSettings.groupDeleted', 'Group deleted.'));
      setConfirmDelete(false);
      onDelete(group.group);
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        setConfirmDelete(false);
        return;
      }
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: 1 }}
      >
        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>
          {group.group}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={group.active}
                disabled={busy}
                onChange={(e) => void handleToggleActive(e.target.checked)}
              />
            }
            label={t('integrationSettings.active', 'Active')}
          />
          <Button
            size="small"
            color="error"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
          >
            {t('integrationSettings.deleteGroup', 'Delete group')}
          </Button>
        </Stack>
      </Stack>

      {/* Prefer the backend's field message (blank/oversized/invalid/duplicate)
          verbatim; fall back to the generic code mapping when absent. */}
      <ErrorBanner message={saveError?.details.message ?? null} error={saveError} />

      {visibleKeys.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('integrationSettings.noKeys', 'No keys in this group yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table
            size="small"
            aria-label={t('integrationSettings.table.ariaLabel', 'Integration keys')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('integrationSettings.table.key', 'Key')}</TableCell>
                <TableCell>{t('integrationSettings.table.value', 'Value')}</TableCell>
                <TableCell align="right">
                  {t('integrationSettings.table.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleKeys.map((s) => (
                <IntegrationKeyRow
                  key={s.key}
                  setting={s}
                  busy={busy}
                  onSaveValue={(k, isSecret, value) => void handleSaveValue(k, isSecret, value)}
                  onRemove={(k) => void handleRemoveKey(k)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {addOpen ? (
        <Box component="form" onSubmit={(e) => void handleAddKey(e)} noValidate sx={{ mt: 2 }}>
          <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t('integrationSettings.fields.key', 'Key')}
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                error={Boolean(keyValidation)}
                helperText={keyValidation ?? undefined}
                slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                type={newIsSecret ? 'password' : 'text'}
                label={t('integrationSettings.fields.value', 'Value')}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                autoComplete={newIsSecret ? 'new-password' : 'off'}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newIsSecret}
                    onChange={(e) => setNewIsSecret(e.target.checked)}
                  />
                }
                label={t('integrationSettings.fields.isSecret', 'Secret')}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button type="submit" size="small" variant="contained" disabled={busy}>
              {t('integrationSettings.addKey', 'Add key')}
            </Button>
            <Button
              size="small"
              disabled={busy}
              onClick={() => {
                setAddOpen(false);
                setNewKey('');
                setNewValue('');
                setNewIsSecret(false);
                setKeyValidation(null);
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Button size="small" sx={{ mt: 1 }} disabled={busy} onClick={() => setAddOpen(true)}>
          {t('integrationSettings.addKey', 'Add key')}
        </Button>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('integrationSettings.deleteGroupTitle', 'Delete group?')}
        body={t(
          'integrationSettings.deleteGroupBody',
          'This removes the group and all of its keys (including any stored secrets). The tenant’s product backend will no longer read these settings. This cannot be undone.',
        )}
        severity="error"
        confirmLabel={t('integrationSettings.deleteGroup', 'Delete group')}
        busy={busy}
        onConfirm={() => void handleDeleteGroup()}
        onCancel={() => setConfirmDelete(false)}
      />
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Section: load + list groups, add-group, contained loading/empty/error/403.
// ---------------------------------------------------------------------------

export function TenantIntegrationSettings() {
  // Tab page (spec 0054): source the tenant from the workspace route loader the
  // same way the sibling tab pages do (e.g. TenantMembers), not from a prop.
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenantId = data.tenant.id;
  const { t } = useCopy();
  const snack = useSnackbar();
  // `undefined` = not yet loaded; an array = loaded (possibly empty).
  const [groups, setGroups] = useState<IntegrationGroup[] | undefined>(undefined);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  // Client-side search (spec 0054): filters displayed keys across all groups by a
  // case-insensitive substring on the key NAME or a NON-SECRET value. Secret values
  // are masked → never matched by value; a secret key still matches by its name.
  const [search, setSearch] = useState('');

  // Add-group form.
  const [addOpen, setAddOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupActive, setNewGroupActive] = useState(true);
  const [groupValidation, setGroupValidation] = useState<string | null>(null);
  const [addError, setAddError] = useState<ApiError | null>(null);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoadError(null);
    try {
      const result = await apiAdmin.integrationSettings.listGroups(tenantId);
      setGroups(result);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setGroups([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setGroups([]);
    }
  }

  useEffect(() => {
    // `load()` only setStates after an `await`; wrap it in an inline async IIFE
    // so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  function replaceGroup(updated: IntegrationGroup) {
    setGroups((prev) =>
      prev ? prev.map((g) => (g.group === updated.group ? updated : g)) : prev,
    );
  }

  function removeGroup(name: string) {
    setGroups((prev) => (prev ? prev.filter((g) => g.group !== name) : prev));
  }

  function handleForbidden() {
    setForbidden(true);
  }

  async function handleAddGroup(e: FormEvent) {
    e.preventDefault();
    setGroupValidation(null);
    setAddError(null);
    const trimmed = newGroupName.trim();
    const nameErr = validateName(trimmed);
    if (nameErr) {
      setGroupValidation(nameErrorText(t, nameErr));
      return;
    }
    if (groups?.some((g) => g.group === trimmed)) {
      setGroupValidation(
        t('integrationSettings.errors.duplicateGroup', 'A group with that name already exists.'),
      );
      return;
    }
    setAdding(true);
    try {
      const created = await apiAdmin.integrationSettings.upsertGroup(tenantId, trimmed, {
        active: newGroupActive,
        entries: [],
      });
      setGroups((prev) => [...(prev ?? []), created]);
      snack.success(t('integrationSettings.groupAdded', 'Group added.'));
      setNewGroupName('');
      setNewGroupActive(true);
      setAddOpen(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) setAddError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setAdding(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h2" component="h2" gutterBottom>
          {t('integrationSettings.title', 'Integration Settings (pass-through)')}
        </Typography>
        <Alert severity="warning">
          {t(
            'integrationSettings.superAdminRequired',
            'Super admin required to manage integration settings.',
          )}
        </Alert>
      </Paper>
    );
  }

  // Case-insensitive substring filter. A key matches by its NAME or, only when it
  // is NOT a secret, by its readable value (secret values are masked — there is no
  // value to match). With no active query every key matches.
  const query = search.trim().toLowerCase();
  function keyMatches(s: IntegrationSetting): boolean {
    if (!query) return true;
    if (s.key.toLowerCase().includes(query)) return true;
    if (!s.isSecret && s.value.toLowerCase().includes(query)) return true;
    return false;
  }
  // Pair each group with its matching keys; while searching, drop groups that have
  // no matching key. The full `group` (with all its keys) still flows to the card so
  // mutations preserve hidden keys.
  const visibleGroups = (groups ?? [])
    .map((g) => ({ group: g, visibleKeys: g.keys.filter(keyMatches) }))
    .filter(({ visibleKeys }) => !query || visibleKeys.length > 0);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('integrationSettings.title', 'Integration Settings (pass-through)')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t(
          'integrationSettings.explanation',
          "Generic per-tenant integration configuration the tenant's product backend reads. saas-admin stores these opaque values and does not interpret them. Values marked secret are write-only — shown only as a masked suffix after saving. This is separate from the GoHighLevel integration above.",
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {groups === undefined ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Stack>
      ) : (
        <>
          {groups.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('integrationSettings.empty', 'No integration settings configured for this tenant yet.')}
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t(
                  'integrationSettings.searchPlaceholder',
                  'Search keys by name or value',
                )}
                slotProps={{
                  htmlInput: {
                    'aria-label': t('integrationSettings.searchAriaLabel', 'Search integration keys'),
                  },
                }}
                sx={{ mb: 2 }}
              />
              {visibleGroups.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('integrationSettings.noMatches', 'No keys match your search.')}
                </Alert>
              ) : (
                <Stack spacing={2} sx={{ mb: 2 }}>
                  {visibleGroups.map(({ group, visibleKeys }) => (
                    <IntegrationGroupCard
                      key={group.group}
                      tenantId={tenantId}
                      group={group}
                      visibleKeys={visibleKeys}
                      onChange={replaceGroup}
                      onDelete={removeGroup}
                      onForbidden={handleForbidden}
                    />
                  ))}
                </Stack>
              )}
            </>
          )}

          {addOpen ? (
            <Box component="form" onSubmit={(e) => void handleAddGroup(e)} noValidate>
              <ErrorBanner message={addError?.details.message ?? null} error={addError} />
              <Grid container spacing={2} sx={{ alignItems: 'flex-start' }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('integrationSettings.fields.groupName', 'Group name')}
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    error={Boolean(groupValidation)}
                    helperText={groupValidation ?? undefined}
                    slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newGroupActive}
                        onChange={(e) => setNewGroupActive(e.target.checked)}
                      />
                    }
                    label={t('integrationSettings.active', 'Active')}
                  />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  disabled={adding}
                  startIcon={adding ? <CircularProgress size={16} /> : null}
                >
                  {t('integrationSettings.addGroup', 'Add group')}
                </Button>
                <Button
                  size="small"
                  disabled={adding}
                  onClick={() => {
                    setAddOpen(false);
                    setNewGroupName('');
                    setNewGroupActive(true);
                    setGroupValidation(null);
                    setAddError(null);
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Button variant="outlined" size="small" onClick={() => setAddOpen(true)}>
              {t('integrationSettings.addGroup', 'Add group')}
            </Button>
          )}
        </>
      )}
    </Paper>
  );
}
